import React, { useState, useEffect } from 'react';
import { bookings } from '../../api';
import './Bookings.css';

export default function LessonTypeWaitlistTab() {
  const navigate = (path) => {
    window.location.hash = path;
  };
  const [waitlists, setWaitlists] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTypes, setExpandedTypes] = useState(new Set());
  const [refreshKey, setRefreshKey] = useState(0);

  const lessonTypeLabels = {
    'technique': '🎾 Technique Training',
    'match-play': '🏆 Match Play',
    'drill-practice': '⚡ Drill Practice',
    'match-prep': '📋 Match Preparation',
    'mental': '🧠 Mental Training',
    'custom': '✨ Other'
  };

  useEffect(() => {
    loadWaitlists();
  }, [refreshKey]);

  const loadWaitlists = async () => {
    try {
      setLoading(true);
      const response = await bookings.getCoachTypeWaitlists();
      setWaitlists(response.data.waitlists || {});
      setError(null);
    } catch (err) {
      console.error('❌ Error loading waitlists:', err);
      setError('Failed to load type waitlists');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (type) => {
    const newExpanded = new Set(expandedTypes);
    if (newExpanded.has(type)) {
      newExpanded.delete(type);
    } else {
      newExpanded.add(type);
    }
    setExpandedTypes(newExpanded);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'waiting':
        return '#3498db';  // blue
      case 'offered':
        return '#f39c12';  // orange
      case 'accepted':
        return '#27ae60';  // green
      case 'declined':
        return '#e74c3c';  // red
      default:
        return '#999';
    }
  };

  const getStatusEmoji = (status) => {
    switch (status) {
      case 'waiting':
        return '⏳';
      case 'offered':
        return '🎉';
      case 'accepted':
        return '✅';
      case 'declined':
        return '❌';
      default:
        return '❓';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="booking-tab-content">
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          ⏳ Loading type waitlists...
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="booking-tab-content">
        <div style={{ textAlign: 'center', padding: '40px', color: '#e74c3c' }}>
          ⚠️ {error}
          <br />
          <button
            onClick={loadWaitlists}
            style={{
              marginTop: '15px',
              padding: '8px 16px',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  const totalEntries = Object.values(waitlists).reduce((sum, list) => sum + list.length, 0);

  if (totalEntries === 0) {
    return (
      <div className="booking-tab-content">
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>📭</div>
          No players on waitlists
          <br />
          <span style={{ fontSize: '14px' }}>Players will appear here when they join type-specific waitlists</span>
        </div>
      </div>
    );
  }

  // Main content
  return (
    <div className="booking-tab-content">
      {/* Header with stats */}
      <div style={{ padding: '15px 20px', background: '#f8f9fa', borderBottom: '1px solid #eee' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <strong>{Object.keys(waitlists).length}</strong> lesson types with waitlists
          </div>
          <button
            onClick={() => setRefreshKey(prev => prev + 1)}
            style={{
              padding: '6px 12px',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Waitlists by type */}
      <div style={{ overflowY: 'auto' }}>
        {Object.entries(waitlists).map(([type, entries]) => (
          <div key={type} style={{ borderBottom: '1px solid #eee' }}>
            {/* Type Header */}
            <button
              onClick={() => toggleExpanded(type)}
              style={{
                width: '100%',
                padding: '15px 20px',
                background: expandedTypes.has(type) ? '#f0f7ff' : 'white',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '15px',
                fontWeight: '600',
                transition: 'background 0.2s'
              }}
            >
              <div style={{ textAlign: 'left' }}>
                <div>{lessonTypeLabels[type] || type}</div>
                <div style={{ fontSize: '12px', fontWeight: '400', color: '#666', marginTop: '3px' }}>
                  {entries.length} {entries.length === 1 ? 'person' : 'people'} waiting
                </div>
              </div>
              <span style={{ fontSize: '18px' }}>
                {expandedTypes.has(type) ? '▼' : '▶'}
              </span>
            </button>

            {/* Expanded content */}
            {expandedTypes.has(type) && (
              <div style={{ background: '#f9f9f9' }}>
                {entries.map((entry, index) => (
                  <div
                    key={entry._id}
                    style={{
                      padding: '12px 20px',
                      borderTop: index === 0 ? 'none' : '1px solid #eee',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px'
                    }}
                  >
                    {/* Position number */}
                    <div
                      style={{
                        background: getStatusColor(entry.status),
                        color: 'white',
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600',
                        fontSize: '12px',
                        flexShrink: 0
                      }}
                    >
                      {entry.position}
                    </div>

                    {/* Player info - clickable to view profile */}
                    <div
                      onClick={() => navigate(`/player/${entry.playerId}`)}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        cursor: 'pointer',
                        transition: 'opacity 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '3px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => navigate(`/player/${entry.playerId}`)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#3498db',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            padding: 0,
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          {entry.playerName}
                        </button>
                        {entry.playerRating && (
                          <span style={{ fontSize: '12px', color: '#f39c12', fontWeight: 'bold' }}>
                            ⭐ {entry.playerRating.toFixed(1)}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '3px' }}>
                        {entry.playerEmail}
                      </div>
                      {entry.anchorRating !== null && entry.ratingVariability !== null && (
                        <div style={{ fontSize: '11px', color: '#8e44ad', marginBottom: '3px' }}>
                          📊 Match: {entry.anchorRating.toFixed(1)} ±{entry.ratingVariability}
                        </div>
                      )}
                      <div style={{ fontSize: '11px', color: '#999' }}>
                        Joined: {new Date(entry.joinedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      {entry.notes && (
                        <button
                          onClick={() => navigate(`/player/${entry.playerId}`)}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '12px',
                            color: '#3498db',
                            marginTop: '5px',
                            fontStyle: 'italic',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            padding: 0,
                            textAlign: 'left'
                          }}
                        >
                          💬 "{entry.notes}"
                        </button>
                      )}
                    </div>

                    {/* Status badge */}
                    <div
                      style={{
                        padding: '4px 10px',
                        background: getStatusColor(entry.status),
                        color: 'white',
                        borderRadius: '3px',
                        fontSize: '11px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                    >
                      {getStatusEmoji(entry.status)} {entry.status.replace('-', ' ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Info section */}
      <div
        style={{
          padding: '15px 20px',
          background: '#f0f7ff',
          borderTop: '1px solid #ddd',
          fontSize: '12px',
          color: '#333'
        }}
      >
        <strong>💡 How it works:</strong>
        <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
          <li>Players join waitlists for specific lesson types they're interested in</li>
          <li>When you create a slot of that type, the first waiting player gets offered it</li>
          <li>They have 24 hours to accept - if they decline, the next person is offered</li>
          <li>Positions update automatically as people accept/decline</li>
        </ul>
      </div>
    </div>
  );
}
